/** Minimal typed fetch wrapper for the dashboard API (/api). Auth is the httpOnly session cookie. */

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public body?: unknown,
  ) {
    super(message);
  }
}

interface Issue {
  path?: (string | number)[];
  message?: string;
}

function errorMessage(status: number, body: unknown): { message: string; code?: string } {
  if (body && typeof body === 'object') {
    const b = body as { message?: string; error?: string; issues?: Issue[] };
    if (b.message) return { message: b.message, code: b.error };
    if (Array.isArray(b.issues) && b.issues.length) {
      const text = b.issues
        .slice(0, 4)
        .map((i) => (i.path?.length ? `${i.path.join('.')}: ${i.message}` : i.message))
        .join('; ');
      return { message: text, code: b.error };
    }
    if (b.error) return { message: b.error.replace(/_/g, ' '), code: b.error };
  }
  return { message: `Request failed (HTTP ${status})` };
}

const AUTH_PAGES = ['/login', '/signup'];

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the automatic redirect to /login on 401 (used by the login form and session bootstrap). */
  noRedirect?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let url = path;
  if (opts.query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (isForm) body = opts.body as FormData;
    else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
  }

  const res = await fetch(url, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers,
    body,
    credentials: 'include',
    signal: opts.signal,
  });

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401 && !opts.noRedirect && !AUTH_PAGES.includes(window.location.pathname)) {
      const next = window.location.pathname + window.location.search;
      window.location.assign(`/login?next=${encodeURIComponent(next)}`);
    }
    const { message, code } = errorMessage(res.status, data);
    throw new ApiError(message, res.status, code, data);
  }
  return data as T;
}

export const get = <T>(path: string, query?: RequestOptions['query']) => api<T>(path, { query });
export const post = <T>(path: string, body: unknown = {}) => api<T>(path, { method: 'POST', body });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

export const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));
